import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

export default function AccountPage() {
  const { user: authUser, refreshUser } = useAuth();
  const [user, setUser] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetState, setResetState] = useState(0); // 0=idle, 1=sending, 2=sent, 3=error
  const [isDirty, setIsDirty] = useState(false);
  const [generalOpen, setGeneralOpen] = useState(true);
  const [securityOpen, setSecurityOpen] = useState(true);

  useEffect(() => {
    async function fetchAccount() {
      try {
        const res = await api.get('/account');
        setUser(res.data.user);
        setOriginal(JSON.stringify(res.data.user));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    fetchAccount();
  }, []);

  const updateUser = (field, value) => {
    setUser(prev => {
      const updated = { ...prev, [field]: value };
      setIsDirty(JSON.stringify(updated) !== original);
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/account', { name: user.name, email: user.email });
      setOriginal(JSON.stringify(res.data.user));
      setUser(res.data.user);
      setIsDirty(false);
      await refreshUser();
      toast.success('Account updated successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving account.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReset = async () => {
    if (resetState !== 0) return;
    setResetState(1);
    try {
      await api.post('/account/send-reset-email');
      setResetState(2);
      setTimeout(() => setResetState(0), 5000);
    } catch (e) {
      setResetState(3);
    }
  };

  const resetBtnText = ['Send Password Reset', 'Sending...', 'Sent!', 'Error'][resetState];

  if (loading) {
    return (
      <ProtectedLayout>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
          <div className="skeleton" style={{ height: '200px', borderRadius: '12px' }}></div>
        </div>
      </ProtectedLayout>
    );
  }

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px', paddingBottom: isDirty ? '100px' : '24px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#0F172A' }}>My Account</h1>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* General Settings */}
          <div className="accordion-section">
            <div className="accordion-trigger" onClick={() => setGeneralOpen(!generalOpen)}>
              <span style={{ fontWeight: '600', fontSize: '15px' }}>General Settings</span>
              <i className={`fas fa-chevron-${generalOpen ? 'up' : 'down'} text-sm text-slate-400`}></i>
            </div>
            {generalOpen && (
              <div className="accordion-content">
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Name</label>
                  <input
                    className="input-field"
                    type="text"
                    value={user?.name || ''}
                    onChange={e => updateUser('name', e.target.value)}
                    placeholder="Full Name"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Email</label>
                  <input
                    className="input-field"
                    type="email"
                    value={user?.email || ''}
                    onChange={e => updateUser('email', e.target.value)}
                    placeholder="Email Address"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Security */}
          <div className="accordion-section">
            <div className="accordion-trigger" onClick={() => setSecurityOpen(!securityOpen)}>
              <span style={{ fontWeight: '600', fontSize: '15px' }}>Security</span>
              <i className={`fas fa-chevron-${securityOpen ? 'up' : 'down'} text-sm text-slate-400`}></i>
            </div>
            {securityOpen && (
              <div className="accordion-content">
                <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>Password</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    className="btn-secondary"
                    onClick={handleSendReset}
                    disabled={resetState !== 0}
                    style={{ opacity: resetState !== 0 ? 0.7 : 1 }}
                  >
                    {resetBtnText}
                  </button>
                  <span style={{ fontSize: '13px', color: '#64748B' }}>Can't log in? Send a password reset link to your email.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save banner */}
      {isDirty && (
        <div className="save-banner" data-testid="dirty-save-banner">
          <div style={{ fontSize: '14px', color: '#334155', fontWeight: '500' }}>
            <i className="fas fa-circle-info mr-2 text-blue-400"></i>
            You have unsaved changes
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn-secondary" style={{ height: '36px', padding: '0 16px' }} onClick={() => { setUser(JSON.parse(original)); setIsDirty(false); }}>
              Discard
            </button>
            <button className="btn-primary" style={{ height: '36px', padding: '0 16px' }} onClick={handleSave} disabled={saving} data-testid="settings-save-button">
              {saving ? <><i className="fas fa-spinner fa-spin mr-1"></i>Saving...</> : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </ProtectedLayout>
  );
}
